import React, { useEffect, useState, useCallback } from "react";
import { X, Printer, Clock, User, Calendar, Wallet, Banknote, AlertCircle, CheckCircle } from "lucide-react";
import { getShiftDetail, getShiftSummary } from "../../api/shiftService";
import { configService } from "../../api/configService";
import { executePrint } from "../../utils/printManager";

const fmt = (n) => Number(n || 0).toLocaleString("id-ID");

const formatDateTime = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("id-ID", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

export default function ReportShiftDetailModal({ shift, onClose }) {
  const [detail, setDetail] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [storeInfo, setStoreInfo] = useState({
    name: localStorage.getItem("tenantName") || "Toko",
    address: "", phone: "", logoUrl: "",
  });

  const fetchDetail = useCallback(() => {
    if (!shift?.shift_id && !shift?.id) return;
    const id = shift.shift_id || shift.id;
    setLoading(true);
    Promise.all([
      getShiftDetail(id).catch(() => null),
      getShiftSummary(id).catch(() => null)
    ])
      .then(([detailRes, summaryRes]) => {
        setDetail(detailRes?.data?.data ?? detailRes?.data ?? detailRes ?? shift);
        setSummary(summaryRes?.data?.data ?? summaryRes?.data ?? summaryRes ?? null);
      })
      .finally(() => setLoading(false));
  }, [shift]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  useEffect(() => {
    let mounted = true;
    configService.getStore()
      .then((res) => {
        const store = res?.data ?? res ?? {};
        if (!mounted) return;
        setStoreInfo({
          name: store?.name || localStorage.getItem("tenantName") || "Toko",
          address: store?.settings?.address || store?.address || "",
          phone: store?.settings?.phone || store?.phone || "",
          logoUrl: store?.logo_url || localStorage.getItem("tenantLogoUrl") || "",
        });
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  if (!shift) return null;

  const d = detail || shift;
  const isClosed = d.status === "CLOSED";

  const handlePrint = () => {
    if (!d) return;
    const receiptData = {
      storeName: storeInfo.name,
      storeAddress: storeInfo.address,
      storePhone: storeInfo.phone,
      storeLogoUrl: "",
      shift: {
        ...d,
        summary: summary || d.summary || d
      }
    };
    executePrint(receiptData, 'shift')
      .then((res) => {
        // Berhasil print
      })
      .catch((err) => {
        alert("Gagal cetak laporan: " + err.message);
      });
  };

  return (
    <div onClick={onClose} className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 modal-backdrop-enter">
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl ring-1 ring-slate-200/70 shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden modal-panel-enter">
        {/* HERO */}
        <div className="relative bg-gradient-to-br from-teal-600 via-teal-700 to-emerald-800 px-6 py-5 text-white overflow-hidden">
          <div className="absolute -top-16 -right-16 w-56 h-56 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-12 -left-8 w-48 h-48 bg-emerald-300/15 rounded-full blur-3xl" />
          <div className="relative flex items-start justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-12 h-12 rounded-xl bg-white/15 ring-1 ring-white/25 flex items-center justify-center flex-shrink-0">
                <Clock className="w-6 h-6" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-teal-100/80 mb-0.5">Detail Laporan Shift</p>
                <h2 className="text-lg lg:text-xl font-bold leading-tight truncate">{d?.shift_number || "-"}</h2>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold ring-1 ${isClosed ? 'bg-emerald-500/20 text-emerald-100 ring-emerald-400/30' : 'bg-amber-500/20 text-amber-100 ring-amber-400/30'}`}>
                    {isClosed ? <CheckCircle className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                    {d?.status}
                  </span>
                </div>
              </div>
            </div>
            <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-lg text-white/80 hover:text-white hover:bg-white/15 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* BODY */}
        <div className="flex-1 overflow-y-auto bg-slate-50/40 p-5">
          {loading ? (
            <div className="flex justify-center py-10 text-slate-500">Memuat detail shift...</div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <InfoCell icon={User} label="Kasir" value={d?.opened_by_name || "-"} />
                <InfoCell icon={Calendar} label="Waktu Buka" value={formatDateTime(d?.opened_at)} />
                <InfoCell icon={Calendar} label="Waktu Tutup" value={formatDateTime(d?.closed_at)} />
                <InfoCell icon={Banknote} label="Saldo Awal" value={`Rp ${fmt(d?.opening_cash)}`} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white rounded-xl ring-1 ring-slate-200/70 p-4">
                  <h3 className="text-sm font-bold text-slate-800 mb-3 border-b border-slate-100 pb-2">Pendapatan Shift</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-slate-600">Tunai</span><span className="font-semibold text-slate-900">Rp {fmt(summary?.cash_sales ?? d?.total_cash_sales)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-600">Non-Tunai</span><span className="font-semibold text-slate-900">Rp {fmt(summary?.non_cash_sales ?? d?.total_non_cash_sales)}</span></div>
                    <div className="pt-2 mt-2 border-t border-slate-100 flex justify-between"><span className="text-slate-800 font-bold">Total Penjualan</span><span className="font-bold text-emerald-600">Rp {fmt((summary?.cash_sales ?? d?.total_cash_sales ?? 0) + (summary?.non_cash_sales ?? d?.total_non_cash_sales ?? 0))}</span></div>
                  </div>
                </div>

                <div className="bg-white rounded-xl ring-1 ring-slate-200/70 p-4">
                  <h3 className="text-sm font-bold text-slate-800 mb-3 border-b border-slate-100 pb-2">Perhitungan Kas Fisik</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-slate-600">Kas Awal</span><span className="font-semibold text-slate-900">Rp {fmt(d?.opening_cash)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-600">Penjualan Tunai</span><span className="font-semibold text-slate-900">+ Rp {fmt(summary?.cash_sales ?? d?.total_cash_sales)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-600">Kas Masuk Laci</span><span className="font-semibold text-slate-900">+ Rp {fmt(d?.total_cash_in || 0)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-600">Kas Keluar Laci</span><span className="font-semibold text-slate-900">- Rp {fmt(d?.total_cash_out || 0)}</span></div>
                    <div className="pt-2 mt-2 border-t border-slate-100 flex justify-between"><span className="text-slate-800 font-bold">Ekspektasi Kas (Sistem)</span><span className="font-bold text-slate-900">Rp {fmt(d?.expected_cash)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-800 font-bold">Kas Aktual (Fisik)</span><span className="font-bold text-teal-600">Rp {fmt(d?.closing_cash || d?.actual_cash)}</span></div>
                    <div className="pt-2 mt-2 border-t border-slate-100 flex justify-between"><span className="text-slate-800 font-bold">Selisih</span><span className={`font-bold ${d?.cash_difference < 0 ? 'text-rose-600' : 'text-emerald-600'}`}>Rp {fmt(d?.cash_difference)}</span></div>
                  </div>
                </div>
              </div>

              {(d?.cash_entries?.length > 0) && (
                <div className="bg-white rounded-xl ring-1 ring-slate-200/70 p-4">
                  <h3 className="text-sm font-bold text-slate-800 mb-3 border-b border-slate-100 pb-2">Riwayat Kasir (In/Out)</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 border-b border-slate-100">
                        <tr><th className="px-3 py-2 text-left">Waktu</th><th className="px-3 py-2 text-left">Keterangan</th><th className="px-3 py-2 text-left">Tipe</th><th className="px-3 py-2 text-right">Jumlah</th></tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {d.cash_entries.map(e => (
                          <tr key={e.id}>
                            <td className="px-3 py-2 text-slate-500">{formatDateTime(e.created_at)}</td>
                            <td className="px-3 py-2 text-slate-800">{e.description}</td>
                            <td className="px-3 py-2"><span className={`inline-flex px-2 py-0.5 rounded text-xs font-semibold ${e.type === 'CASH_IN' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>{e.type === 'CASH_IN' ? 'MASUK' : 'KELUAR'}</span></td>
                            <td className="px-3 py-2 text-right font-medium">Rp {fmt(e.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {d?.notes && (
                <div className="bg-white rounded-xl ring-1 ring-slate-200/70 p-4 text-sm">
                  <span className="font-bold text-slate-800">Catatan:</span><p className="text-slate-600 mt-1 whitespace-pre-wrap">{d.notes}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div className="px-5 py-3.5 border-t border-slate-100 bg-white flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg ring-1 ring-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50">Tutup</button>
          <button onClick={handlePrint} disabled={loading || !d} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-semibold hover:bg-teal-700 disabled:opacity-50">
            <Printer className="w-4 h-4" /> Cetak Laporan
          </button>
        </div>
      </div>
    </div>
  );
}

function InfoCell({ icon: Icon, label, value }) {
  return (
    <div className="bg-white ring-1 ring-slate-200/70 rounded-lg px-3 py-2.5 flex items-start gap-2">
      <div className="mt-0.5 text-slate-400"><Icon className="w-4 h-4" /></div>
      <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase text-slate-500">{label}</p>
        <p className="text-sm font-semibold text-slate-800 truncate">{value}</p>
      </div>
    </div>
  );
}
